import ApiClient, { DevicePublishedContent } from '@raydiant/api-client-js';
import { PresentationWindowRenderer } from '@raydiant/playlist-window-manager';
import IFrameRenderer from '@raydiant/playlist-window-manager/build/IFrameRenderer';
import React, { useEffect, useCallback, useState, useRef } from 'react';
import Pubnub from 'pubnub';
import {
  useHeartbeat,
  usePublishedContent,
  useWindowManager,
  useRestart,
  useConnectedScreens,
  useScreenOrientation,
  useAdContent,
} from './hooks';
import SetupStep from './components/SetupStep';

export interface AppProps {
  deviceId: string;
  channelId: string;
  sessionId: string;
  apiClient: ApiClient;
  pubnubClient: Pubnub;
  pauseWhileOffline: boolean;
  appRendererUrl?: string;
  appRendererOfflineSupport?: boolean;
  onReady: () => void;
  onError: (error: Error) => void;
  onDeviceDeleted: () => void;
  logger: Logger;
  rootElement: HTMLElement;
  dashUrl?: string;
  injectAds?: boolean;
  signUpPage?: string;
  signUpQrCode?: React.ReactNode;
}

const isPublishedPlaylistValid = (publishedContent: DevicePublishedContent) => {
  if (!publishedContent.playlistId) return false;
  if (!publishedContent.playlists) return false;

  const playlist = publishedContent.playlists[publishedContent.playlistId];
  if (!playlist) return false;
  if (!playlist.r.resource.deletedAt === null) return false;

  return true;
};

export type BootStep =
  | ''
  | 'register_device'
  | 'waiting_for_publish'
  | 'multiple_screens'
  | 'disabled_screen'
  | 'empty_playlist'
  | 'play_playlist';

export default function App({
  deviceId,
  channelId,
  sessionId,
  apiClient,
  pubnubClient,
  pauseWhileOffline,
  appRendererUrl,
  appRendererOfflineSupport,
  onReady,
  onError,
  onDeviceDeleted,
  rootElement,
  logger,
  injectAds,
  signUpPage,
  signUpQrCode,
}: AppProps): JSX.Element | null {
  // Reloads the page when a restart event is received.
  useRestart(pubnubClient, logger);

  const connectedScreens = useConnectedScreens(
    pubnubClient,
    channelId,
    sessionId,
    logger,
  );

  // Heartbeat is fetched on load, then periodically.
  const [heartbeat, heartbeatError, setHeartbeatInterval] = useHeartbeat(
    apiClient,
    pubnubClient,
    onDeviceDeleted,
    logger,
  );

  // Set screen orientation.
  useScreenOrientation(heartbeat?.screenOrientation, rootElement, logger);

  // Published content is fetched on load, then whenever a publish event is received.
  const [publishedContent, publishedContentError] = usePublishedContent(
    apiClient,
    pubnubClient,
    logger,
  );

  // Create the renderer when the container has been added to the DOM.
  const [renderer, setRenderer] = useState<PresentationWindowRenderer | null>(
    null,
  );
  const createRenderer = useCallback((node: HTMLElement | null) => {
    // Destroy renderer when the node is removed which can happen when transitioning
    // to a boot step from play_playlist. Also ensure we only create one renderer for
    // the lifetime of the node.
    if (!node) {
      setRenderer(null);
    } else if (node) {
      setRenderer(new IFrameRenderer(node));
    }
  }, []);

  // Window manager is created when the device is registered and has published content.
  const [windowManager, isEmptyPlaylist] = useWindowManager(
    deviceId,
    heartbeat,
    publishedContent,
    renderer,
    logger,
    appRendererUrl,
    appRendererOfflineSupport,
  );

  // Figure out the current step in the boot flow.
  let bootStep: BootStep = '';
  // Wait until we've fetched a heartbeat before figuring which step we're on.
  if (heartbeat !== null) {
    if (connectedScreens !== null && connectedScreens >= 1) {
      bootStep = 'multiple_screens';
    } else if (heartbeat.isDisabled) {
      bootStep = 'disabled_screen';
    } else if (
      heartbeat.registrationStatus === 'pending_dashboard_confirmation'
    ) {
      bootStep = 'register_device';
    } else if (
      publishedContent !== null &&
      !isPublishedPlaylistValid(publishedContent)
    ) {
      bootStep = 'waiting_for_publish';
    } else if (windowManager !== null && isEmptyPlaylist) {
      bootStep = 'empty_playlist';
    } else if (windowManager !== null && !isEmptyPlaylist) {
      bootStep = 'play_playlist';
    }
  }

  // Inject ad content into window manager if app type is Lite.
  if (injectAds) {
    useAdContent(bootStep, windowManager, apiClient, logger);
  }

  // Reload the webpage when boot step becomes 'playlist_play' and we're
  // on a previous step like 'waiting_for_publish' or 'multiple_screens'.
  // This prevents a flash of black while the playlist is loading.
  const prevBootStepRef = useRef<BootStep | null>(null);
  useEffect(() => {
    if (
      prevBootStepRef.current !== null &&
      bootStep === 'play_playlist' &&
      bootStep !== prevBootStepRef.current
    ) {
      logger.info(
        `Boot step changed to 'play_playlist' from ${prevBootStepRef.current}, reloading`,
      );
      pubnubClient.unsubscribeAll();
      // NOTE: Reloading here will cause the above log message to never be flushed.
      window.location.reload();
    }

    // Ensure the previous boot step is never reset back to initial state.
    if (bootStep !== '') {
      prevBootStepRef.current = bootStep;
    }
  }, [bootStep, prevBootStepRef.current]);

  // Speed up heartbeat when waiting for registration.
  useEffect(() => {
    if (bootStep === 'register_device') {
      setHeartbeatInterval(10 * 1000);
    } else {
      setHeartbeatInterval(null);
    }
  }, [bootStep, setHeartbeatInterval]);

  // Call onReady when we've determined the correct step.
  useEffect(() => {
    if (bootStep === 'play_playlist') {
      // Don't render the playlist if there's no renderer.
      if (!renderer) return;
      // Don't render the playlist until the window manager had been created.
      if (!windowManager) return;
      // Don't render the playlist if it's already playing.
      if (windowManager.isPlaying()) return;

      // Render the playlist, then trigger play and call onReady.
      logger.info(`Boot step is 'play_playlist', render playlist`);

      windowManager
        .load()
        .then(() => windowManager.show())
        .then(() => windowManager.play())
        .then(onReady);
    } else if (bootStep !== '') {
      // We're showing a setup step, call onReady.
      if (bootStep === 'register_device') {
        logger.info(
          `Boot step is 'register_device', confirmationToken=${heartbeat?.confirmationToken}`,
        );
      } else {
        logger.info(`Boot step is '${bootStep}'`);
      }
      onReady();
    }
  }, [bootStep, windowManager, renderer]);

  // Call onError if there's an error.
  useEffect(() => {
    if (heartbeatError) {
      onError(heartbeatError);
    } else if (publishedContentError) {
      onError(publishedContentError);
    }
  }, [heartbeatError, publishedContentError]);

  // Pause playlist when offline.
  useEffect(() => {
    if (!pauseWhileOffline) return;

    const handleOffline = () => {
      if (windowManager && !windowManager.isPaused()) {
        logger.warn('Network disconnected, pause window manager');
        windowManager.pause();
      }
    };

    const handleOnline = () => {
      if (windowManager && windowManager.isPaused()) {
        logger.info('Network reconnected, resume window manager');
        windowManager.resume();
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [pauseWhileOffline, windowManager, logger]);

  // Don't render anything until we determine the step.
  if (bootStep === '') {
    return null;
  }

  // Render the playlist.
  if (bootStep === 'play_playlist') {
    return <div ref={createRenderer}></div>;
  }

  // Render the setup step.
  return (
    <SetupStep
      deviceId={deviceId}
      bootStep={bootStep}
      confirmationToken={heartbeat?.confirmationToken || null}
      signUpPage={signUpPage}
      signUpQrCode={signUpQrCode}
    />
  );
}
